import React, { forwardRef, useEffect, useRef } from "react";
import styled from "styled-components";
import IEditableProps from "../../utilities/IEditableProps";
import IStyledProps from "../../utilities/IStyledProps";
import classNames from "classnames";
import { getImagePath } from "../../utilities/path";
import { AutomationTestIdFeature, AutomationTestIdElementType, createDataTestId } from "../../utilities/automation";
import Picture from "../Picture";

export interface ITwoImagesProps extends IEditableProps, IStyledProps {
    foregroundImage: string;
    foregroundAlt: string;
    foregroundWidth: number;
    backgroundImage: string;
    backgroundAlt: string;
    backgroundWidth: number;
    backgroundPosition: string;
    offset: number;
    onOffsetChange?: (number) => void;
}

const TwoImages = forwardRef((props: ITwoImagesProps, ref: any) => {
    const parallaxRef = useRef(null); // used for detecting y position of wrapper when page scrolling
    const { className, editorClassObj, cmsId, foregroundImage = '', backgroundImage = '', onOffsetChange = () => {} } = props;

    useEffect(() => {

        function handleScroll() {
            const imagePosition = parallaxRef.current.getBoundingClientRect().top;
            const windowHeight = window.innerHeight;
            const scrollPosition = window.scrollY;
            let translateY = 0;

            if (imagePosition < windowHeight * 0.8 + scrollPosition) {
                const distance = scrollPosition - (imagePosition - windowHeight * 0.8);
                translateY = (distance / (windowHeight * 0.8)) * 12; // Adjust the downward movement
            }

            onOffsetChange(translateY);
            parallaxRef.current.style.transform = "translateY(" + translateY + "px)";
        }

        const options = {
            threshold: 0,
        };

        const handleIntersection = (entries) => {
            // only one element being observed [0]
            if (entries[0]?.isIntersecting) {
                // only monitor scrolling when element is in viewport
                document.addEventListener('scroll', handleScroll);
            } else {
                document.removeEventListener('scroll', handleScroll);
            }
        };

        const observer = new IntersectionObserver(handleIntersection, options);

        if (parallaxRef.current) {
            observer.observe(parallaxRef.current);
        }

        return () => {
            if (parallaxRef.current) {
                observer.unobserve(parallaxRef.current);
            }
        };
    }, []);

    return (
        <div data-id={cmsId} ref={ref} className={classNames(className, editorClassObj, "position-relative text-center")}>
            <Picture
                loading="lazy"
                pictureClassName={classNames("position-absolute background-image d-none d-lg-block", props.backgroundPosition)}
                className="w-100"
                src={getImagePath(backgroundImage)}
                alt={props.backgroundAlt}
                dataTestId={createDataTestId({feature: AutomationTestIdFeature.TWO_IMAGES_PARALLAX, element: AutomationTestIdElementType.IMAGE, specifier: 'background'})}
            ></Picture>
            <div className="parallax-wrapper" ref={parallaxRef} data-test-id={createDataTestId({feature: AutomationTestIdFeature.TWO_IMAGES_PARALLAX, element: AutomationTestIdElementType.CONTAINER, specifier: 'parallax'})}>
                <Picture
                    loading="lazy"
                    pictureClassName={classNames("position-relative foreground-image")}
                    src={getImagePath(foregroundImage)}
                    alt={props.foregroundAlt}
                    style={{width: `${props.foregroundWidth}%`}}
                    dataTestId={createDataTestId({feature: AutomationTestIdFeature.TWO_IMAGES_PARALLAX, element: AutomationTestIdElementType.IMAGE, specifier: 'foreground'})}
                    />
            </div>
        </div>
    )
});

export default styled(TwoImages)`
    .foreground-image {
        transition: all 0.2s ease-out;
    }
    .background-image {
        width: ${props => props.backgroundWidth}%;
    }
    .top-right {
        top: -${props => props.offset}rem;
        right: -${props => props.offset}rem;
    }
    .top-left {
        top: -${props => props.offset}rem;
        left: -${props => props.offset}rem;
    }
    .bottom-right {
        bottom: -${props => props.offset}rem;
        right: -${props => props.offset}rem;
    }
    .bottom-left {
        bottom: -${props => props.offset}rem;
        left: -${props => props.offset}rem;
    }
    @media screen and (max-width: 991px) {
        .parallax-wrapper {
            transform: translateY(0) !important;
        }
    }
`;